import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CurrencyCode } from '@/shared/lib/currency';

import { MinesActiveGame, MinesResult, MinesState } from './types';

const initialState: MinesState = {
  status: 'idle',
  game: null,
  result: null,
  pendingCell: null,
  radarHint: null,
  loading: false,
};

const minesSlice = createSlice({
  name: 'mines',
  initialState,
  reducers: {
    // User actions (sagas listen)
    startGame(
      _state,
      _action: PayloadAction<{ betAmount: number; minesCount: number; currency: CurrencyCode; giftId?: string }>,
    ) {},
    clickCell(_state, _action: PayloadAction<number>) {},
    cashout(_state) {},
    resetGame(state) {
      state.status = 'idle';
      state.game = null;
      state.result = null;
      state.pendingCell = null;
      state.radarHint = null;
      state.loading = false;
    },

    // State mutations
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setPendingCell(state, action: PayloadAction<number | null>) {
      state.pendingCell = action.payload;
    },
    setActiveGame(state, action: PayloadAction<MinesActiveGame>) {
      state.status = 'playing';
      state.game = action.payload;
      state.radarHint = action.payload.radarHint;
      state.result = null;
      state.loading = false;
    },
    cellRevealed(state, action: PayloadAction<{
      revealedPositions: number[];
      currentMultiplier: number;
      nextMultiplier: number | null;
      radarHint: number | null;
    }>) {
      if (!state.game) return;
      state.game.revealedPositions = action.payload.revealedPositions;
      state.game.currentMultiplier = action.payload.currentMultiplier;
      state.game.nextMultiplier = action.payload.nextMultiplier;
      // Preserve existing hint — server sends null on most clicks, overwrite only when a new hint arrives
      if (action.payload.radarHint !== null) state.radarHint = action.payload.radarHint;
      state.pendingCell = null;
    },
    gameBusted(state, action: PayloadAction<MinesResult>) {
      state.status = 'busted';
      state.result = action.payload;
      state.pendingCell = null;
      state.radarHint = null;
      state.loading = false;
    },
    gameCashedOut(state, action: PayloadAction<MinesResult>) {
      state.status = 'won';
      state.result = action.payload;
      state.radarHint = null;
      state.loading = false;
    },
  },
});

export const minesActions = minesSlice.actions;
export const minesReducer = minesSlice.reducer;
